import type { ReactNode } from "react";

import { Banner, Button, clsx, Icon, Select, Spinner } from "@alpacahq/ui/old";

import { cn } from "~/v2/utils/cn";
import {
  RothIRAIcon,
  TraditionalIRAIcon,
} from "src/v2/components/common/IRAIcons";
import { getPrettyCash } from "src/v2/utils/formatting";

const BG_IMG_PATH = "resources/images/bg-ira-contributions.svg";

const ProgressBar = ({
  percentage,
  hasOvercontributed,
}: {
  percentage: number;
  hasOvercontributed: boolean;
}) => (
  <div className="flex h-2 w-full items-center justify-start rounded bg-gray-100">
    <div
      className={cn(
        "h-2 rounded",
        hasOvercontributed ? "bg-orange-500" : "bg-indigo-500",
        "duration-300",
      )}
      style={{ width: `${Math.min(percentage, 100)}%` }}
    />
  </div>
);

const CONTRIBUTION_LIMITS_URL =
  "https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-ira-contribution-limits";

const overcontributionTextMap: Record<
  string,
  (amountText: ReactNode) => ReactNode
> = {
  both: (amountText) => (
    <span>
      You've over-contributed {amountText} across Roth & Traditional accounts.
    </span>
  ),
  roth: (amountText) => (
    <span>You've over-contributed {amountText} to your Roth IRA.</span>
  ),
  traditional: (amountText) => (
    <span>You've over-contributed {amountText} to your Traditional IRA.</span>
  ),
};

const OvercontributionBanner = ({
  type,
  overcontributionAmount,
}: {
  type: string;
  overcontributionAmount: number;
}) => {
  const amountText = (
    <span className="font-mono">{getPrettyCash(overcontributionAmount)}</span>
  );

  const text = overcontributionTextMap[type || "traditional"](amountText);

  return (
    <Banner
      variant="warning"
      message={text}
      button={
        <a
          className="text-sm"
          href={CONTRIBUTION_LIMITS_URL}
          aria-label="Learn more about IRA contribution limits"
        >
          Learn More
        </a>
      }
    />
  );
};

const Container = ({ children }: { children: ReactNode }) => (
  <div
    className={`flex rounded-lg border border-gray-200 px-6 py-4 bg-[url(${BG_IMG_PATH})] bg-no-repeat`}
  >
    {children}
  </div>
);

export const IRAContributionCardLoading = () => (
  <Container>
    <div className={clsx("w-full items-center justify-items-center")}>
      <Spinner />
    </div>
  </Container>
);

interface IRAContributionCardPresProps {
  currentType: string;
  hasBothTypes: boolean;
  ytdAccountContributionAmount: number;
  ytdOverallContributionAmount: number;
  ownerAnnualContributionLimit: number;
  lifetimeOverallContributionAmount: number;
  overcontributionAmount: number;
  ytdAccountContributionPercent: number;
  currentYear: string;
  taxYear: string;
  availableTaxYears: string[];
  onClickAddContribution: () => void;
  onClickTaxYear: (year: string) => void;
}

export const IRAContributionCardPres = ({
  currentType,
  hasBothTypes,
  ytdAccountContributionAmount,
  ytdOverallContributionAmount,
  ownerAnnualContributionLimit,
  lifetimeOverallContributionAmount,
  overcontributionAmount,
  ytdAccountContributionPercent,
  currentYear,
  taxYear,
  availableTaxYears,
  onClickAddContribution,
  onClickTaxYear,
}: IRAContributionCardPresProps) => {
  const hasOvercontributed = overcontributionAmount > 0;

  const title =
    currentType === "traditional" ? "IRA Overview" : "Roth IRA Overview";
  const icon =
    currentType === "traditional" ? <TraditionalIRAIcon /> : <RothIRAIcon />;

  const ytdContributionColor =
    (hasOvercontributed && "text-orange-600") || "text-gray-400";
  const ytdOverallContributionColor =
    (hasOvercontributed && "text-orange-600") ||
    (ytdOverallContributionAmount > 0 && "text-emerald-500") ||
    "text-gray-400";

  return (
    <Container>
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className="m-0 p-0 font-medium text-base text-inherit slashed-zero">
              {title}
            </span>
            <a
              href="https://alpaca.markets/support/tag/ira"
              target="_blank"
              rel="noopener"
            >
              <Icon name="QuestionMarkCircle" />
            </a>
          </div>
          <div className="flex items-center gap-2">
            {availableTaxYears.some((v) => v !== currentYear) && (
              <Select
                value={taxYear}
                options={availableTaxYears.map((year) => ({
                  label: year,
                  value: year,
                }))}
                onChange={onClickTaxYear}
              />
            )}
            <Button
              disabled={hasOvercontributed || taxYear !== currentYear}
              onClick={onClickAddContribution}
            >
              Add Contribution
            </Button>
          </div>
        </div>
        {hasOvercontributed && (
          <OvercontributionBanner
            type={hasBothTypes ? "both" : currentType}
            overcontributionAmount={overcontributionAmount}
          />
        )}
        <div className="flex justify-between">
          <div>
            <span className={`${ytdContributionColor} font-mono text-sm`}>
              {getPrettyCash(ytdAccountContributionAmount, {
                acceptZero: true,
              })}
            </span>
            <span className="font-mono text-gray-700 text-sm">
              /{getPrettyCash(ownerAnnualContributionLimit)}
            </span>
          </div>
          {hasOvercontributed ? (
            <span className="font-mono text-orange-600 text-sm">
              {getPrettyCash(overcontributionAmount)} Over
            </span>
          ) : (
            <span className="font-mono text-gray-700 text-sm">
              {ytdAccountContributionPercent}%
            </span>
          )}
        </div>
        <ProgressBar
          percentage={ytdAccountContributionPercent}
          hasOvercontributed={hasOvercontributed}
        />
        <div className="flex justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-sm">{taxYear} Contributions</span>
            <span
              className={`font-mono text-sm ${ytdOverallContributionColor}`}
            >
              {getPrettyCash(ytdOverallContributionAmount, {
                withPrefix: ytdOverallContributionAmount !== 0,
                acceptZero: true,
              })}
            </span>
          </div>
          <div className="flex flex-col items-start gap-1">
            <span className="text-sm">Lifetime Contributions</span>
            <span className="font-mono text-sm">
              {getPrettyCash(lifetimeOverallContributionAmount)}
            </span>
          </div>
        </div>
      </div>
    </Container>
  );
};
